/**
 * MP Stoper - Main Application
 * Race timer application for recording participant finish times
 */

import { AppState } from './modules/state.js';
import { storage } from './modules/storage.js';
import { Timer, formatTime, getElapsedTime } from './modules/timer.js';
import { UI } from './modules/ui.js';
import { KeyboardHandler } from './modules/keyboard.js';

class MPStoper {
  constructor() {
    this.state = new AppState();
    this.ui = new UI();
    this.timer = null;
    this.keyboard = null;
  }

  /**
   * Initialize application
   */
  async init() {
    // Load saved state
    this.loadSavedState();

    // Setup event listeners
    this.setupEventListeners();

    // Setup keyboard handler
    this.setupKeyboard();

    // Subscribe to state changes
    this.subscribeToState();

    // Hide loading screen
    setTimeout(() => {
      this.ui.hideLoading();
    }, 300);
  }

  /**
   * Load state from localStorage
   */
  loadSavedState() {
    const savedData = {
      startTime: storage.getStartTime(),
      finishTime: storage.getFinishTime(),
      results: storage.getResults(),
    };

    this.state.loadFromStorage(savedData);

    // Display saved results
    if (savedData.results.length > 0) {
      this.ui.showEmptyState(false);
      savedData.results.forEach((result, index) => {
        this.ui.addResultRow(result, savedData.results.length - index);
      });
    }

    // Set initial UI state based on whether race is finished or in progress
    if (savedData.finishTime) {
      // Race is finished - disable numpad
      this.ui.setNumpadEnabled(false);
      this.ui.setFinishButtonEnabled(false);
      this.ui.setStartButtonEnabled(true);
    } else if (savedData.startTime) {
      // Race is in progress - enable numpad
      this.ui.setNumpadEnabled(true);
      this.ui.setFinishButtonEnabled(true);
      this.ui.setStartButtonEnabled(false);
    } else {
      // No race - disable numpad
      this.ui.setNumpadEnabled(false);
      this.ui.setFinishButtonEnabled(false);
      this.ui.setStartButtonEnabled(true);
    }
  }

  /**
   * Setup UI event listeners
   */
  setupEventListeners() {
    // Start button
    this.ui.elements.startBtn.addEventListener('click', () => this.handleStart());

    // Finish button
    this.ui.elements.finishBtn.addEventListener('click', () => this.handleFinish());

    // Save button
    this.ui.elements.saveBtn.addEventListener('click', () => this.handleSave());

    // Clear button
    this.ui.elements.clearBtn.addEventListener('click', () => this.handleClear());

    // Copy button
    this.ui.elements.copyBtn.addEventListener('click', () => this.handleCopyResults());

    // Numpad buttons
    this.ui.elements.numpadButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const number = btn.getAttribute('data-number');
        this.handleNumberInput(number);
      });
    });
  }

  /**
   * Setup keyboard shortcuts
   */
  setupKeyboard() {
    this.keyboard = new KeyboardHandler(
      number => this.handleNumberInput(number),
      () => this.handleSave(),
      () => this.handleClear()
    );
    this.keyboard.init();

    if (this.state.isRaceStarted) {
      this.keyboard.enable();
    }
  }

  /**
   * Subscribe to state changes
   */
  subscribeToState() {
    // Race started
    this.state.subscribe('raceStarted', data => {
      this.ui.setStartButtonEnabled(false);
      this.ui.setFinishButtonEnabled(true);
      this.ui.setNumpadEnabled(true);
      this.ui.updateStartTime(data.startTime);
      this.keyboard.enable();

      // Start timer if not restored
      if (!data.restored) {
        this.ui.clearResults();
        this.ui.showEmptyState(true);
      }

      // Start display timer
      this.startDisplayTimer(data.startTime);
    });

    // Race finished
    this.state.subscribe('raceFinished', () => {
      this.ui.setStartButtonEnabled(true);
      this.ui.setFinishButtonEnabled(false);
      this.ui.setNumpadEnabled(false);
      this.keyboard.disable();

      // Stop display timer
      if (this.timer) {
        this.timer.stop();
      }
    });

    // Current number changed
    this.state.subscribe('currentNumberChanged', number => {
      this.ui.updateCurrentNumber(number);
    });

    // Result added
    this.state.subscribe('resultAdded', result => {
      this.ui.showEmptyState(false);
      this.ui.addResultRow(result, this.state.results.length);
      this.ui.vibrate();
      this.ui.setCopyButtonEnabled(true);
    });

    // State cleared
    this.state.subscribe('stateCleared', () => {
      this.ui.resetTimer();
      this.ui.updateCurrentNumber('');
      this.ui.setCopyButtonEnabled(false);
    });

    // Results changed - enable copy if there are results
    this.state.subscribe('resultsChanged', results => {
      this.ui.setCopyButtonEnabled(results.length > 0);
    });
  }

  /**
   * Start display timer
   */
  startDisplayTimer(startTime) {
    if (this.timer) {
      this.timer.stop();
    }

    this.timer = new Timer(() => {
      this.ui.updateTimer(startTime);
    }, 1000);

    this.timer.start();
  }

  /**
   * Handle start button click
   */
  handleStart() {
    const timestamp = Date.now();

    // Clear previous data
    storage.clearAll();
    this.state.clear();

    // Start new race
    storage.saveStartTime(timestamp);
    this.state.startRace(timestamp);
  }

  /**
   * Handle finish button click
   */
  handleFinish() {
    const timestamp = Date.now();

    storage.saveFinishTime(timestamp);
    this.state.finishRace(timestamp);
  }

  /**
   * Handle number input
   */
  handleNumberInput(number) {
    if (!this.state.isRaceStarted) return;

    const newNumber = this.state.currentNumber + number;
    this.state.setCurrentNumber(newNumber);
    this.ui.vibrate();
  }

  /**
   * Handle clear button (backspace)
   */
  handleClear() {
    if (!this.state.isRaceStarted) return;

    const currentNumber = this.state.currentNumber;
    if (currentNumber.length > 0) {
      const newNumber = currentNumber.slice(0, -1);
      this.state.setCurrentNumber(newNumber);
    }
  }

  /**
   * Handle save button
   */
  handleSave() {
    if (!this.state.isRaceStarted || !this.state.currentNumber) return;

    const timestamp = Date.now();
    const elapsed = getElapsedTime(this.state.startTime, timestamp);
    const formattedTime = formatTime(elapsed);

    const result = {
      number: this.state.currentNumber,
      time: formattedTime,
      timestamp: timestamp,
    };

    // Add to state and storage
    this.state.addResult(result);
    storage.saveResults(this.state.results);

    // Clear current number
    this.state.setCurrentNumber('');
  }

  /**
   * Handle copy results button
   */
  async handleCopyResults() {
    if (this.state.results.length === 0) return;

    // Format results as text
    let text = 'MP Stoper - Wyniki\n';
    text += '==================\n\n';

    // Add each result
    this.state.results.slice().reverse().forEach((result, index) => {
      const placement = index + 1;
      text += `${placement}. Numer: ${result.number} - Czas: ${result.time}\n`;
    });

    text += `\nLiczba uczestników: ${this.state.results.length}`;

    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(text);
      this.ui.showCopyNotification();
      this.ui.vibrate();
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      this.fallbackCopy(text);
    }
  }

  /**
   * Fallback copy method for older browsers
   */
  fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand('copy');
      this.ui.showCopyNotification();
      this.ui.vibrate();
    } catch (err) {
      console.error('Fallback copy failed:', err);
    }

    document.body.removeChild(textarea);
  }
}

// Initialize app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const app = new MPStoper();
    app.init();
  });
} else {
  const app = new MPStoper();
  app.init();
}
